import type { FC } from 'react';
import { useRef, useState } from 'react';
import { cookiePopupClosed, dispatchCustomEvent } from '../../../../js/CustomEvents/CustomEvents';
import Button from '../../../general/Button/Button';
import type { CookieDialogTranslations } from '../../types';
import useCookieBannerBlockerHandler from '../../Hooks/UseCookieBannerBlockerHandler';
import Dialog from '../../../../stories/Molecules/Dialog/Dialog';

interface Props {
    onDenyConsent: (blocked: boolean) => void;
    onGrantConsent: () => void;
    privacyPolicyLink: string;
    translations: CookieDialogTranslations;
}

/**
 * The final part only contains a dot, but we'll include it, so the explanation doesn't miss any characters if they decide to switch around the placeholders in the translation.
 */
const getExplanationTexts = (explanationText: string): [string, string, string] => explanationText.split(/{denyLink}|{privacyPolicyLink}/) as [string, string, string];

const CookieDialog: FC<Props> = ({ onDenyConsent, onGrantConsent, privacyPolicyLink, translations }) => {
    const dialogRef = useRef<HTMLDialogElement>(null);
    const [showDialog, setShowDialog] = useState(true);

    const explanationTexts = getExplanationTexts(translations.cookieExplanation);

    const closeDialog = () => {
        setShowDialog(false);
        dispatchCustomEvent(cookiePopupClosed());
    };

    const denyConsent = (blocked = false) => {
        onDenyConsent(blocked);
        closeDialog();
    };

    const grantConsent = () => {
        onGrantConsent();
        closeDialog();
    };

    useCookieBannerBlockerHandler({
        element: dialogRef,
        onBlock: () => denyConsent(true),
    });

    return (
        <Dialog open={showDialog} dialogRef={dialogRef}>
            <Dialog.Body>
                <div className="cookie-notification__content">
                    <p className="lh-md">
                        <span>{explanationTexts[0]}</span>
                        <span className="consent-link" onClick={() => denyConsent()}>{translations.declineCookiesButton}</span>
                        <span>{explanationTexts[1]}</span>
                        <a href={privacyPolicyLink}>{translations.privacyPolicy}</a>
                        <span>{explanationTexts[2]}</span>
                    </p>
                    <Button isPrimary autoFocus onClick={grantConsent} className="mt-4">{translations.acceptCookiesButton}</Button>
                </div>
            </Dialog.Body>
        </Dialog>
    );
};

export default CookieDialog;
